import * as React from 'react'
import { createPortal } from 'react-dom'
import {
    arrow,
    autoUpdate,
    flip,
    offset as offsetMiddleware,
    shift,
    useFloating,
    type Placement,
} from '@floating-ui/react-dom'
import classNames from 'classnames'

/**
 * Drop-in replacement for `Tooltip2` from `@blueprintjs/popover2`.
 *
 * Goal: keep the Blueprint API surface so callsites can switch by changing
 * the import. The visual is hand-tuned to match Blueprint's dark tooltip.
 *
 * Supported props (mapped from `Tooltip2`):
 *   - content                 → tooltip body
 *   - position                → '<side>[-start|-end]' (default 'auto' → top)
 *   - hoverOpenDelay          → ms before opening on hover (default 100)
 *   - hoverCloseDelay         → ms before closing after leave (default 0)
 *   - disabled                → tooltip never opens
 *   - openOnTargetFocus       → also open on focus (default true)
 *   - interactionKind         → 'hover' | 'hover-target' | 'click' (default 'hover-target')
 *   - wrapperTagName          → wrapper element tag (default 'span')
 *   - popoverClassName        → className on the floating tooltip element
 *   - className               → className on the wrapper
 *   - portalContainer         → element to portal into (default document.body)
 *   - onOpening / onOpened / onClosed → lifecycle callbacks
 *   - children                → trigger
 */

type TooltipPosition =
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-left'
    | 'top-right'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right'
    | 'left'
    | 'left-top'
    | 'left-bottom'
    | 'right'
    | 'right-top'
    | 'right-bottom'

type InteractionKind = 'hover' | 'hover-target' | 'click'

export interface TooltipProps {
    content: React.ReactNode
    children: React.ReactNode
    position?: TooltipPosition
    hoverOpenDelay?: number
    hoverCloseDelay?: number
    disabled?: boolean
    openOnTargetFocus?: boolean
    interactionKind?: InteractionKind
    wrapperTagName?: keyof JSX.IntrinsicElements
    popoverClassName?: string
    className?: string
    portalContainer?: HTMLElement
    onOpening?: () => void
    onOpened?: () => void
    onClosed?: () => void
}

const positionToPlacement: Record<TooltipPosition, Placement> = {
    auto: 'top',
    'auto-start': 'top-start',
    'auto-end': 'top-end',
    top: 'top',
    'top-left': 'top-start',
    'top-right': 'top-end',
    bottom: 'bottom',
    'bottom-left': 'bottom-start',
    'bottom-right': 'bottom-end',
    left: 'left',
    'left-top': 'left-start',
    'left-bottom': 'left-end',
    right: 'right',
    'right-top': 'right-start',
    'right-bottom': 'right-end',
}

const Tooltip = React.forwardRef<HTMLElement, TooltipProps>(function Tooltip(
    {
        content,
        children,
        position = 'auto',
        hoverOpenDelay = 100,
        hoverCloseDelay = 0,
        disabled = false,
        openOnTargetFocus = true,
        interactionKind = 'hover-target',
        wrapperTagName = 'span',
        popoverClassName,
        className,
        portalContainer,
        onOpening,
        onOpened,
        onClosed,
    },
    forwardedRef,
) {
    const [open, setOpen] = React.useState(false)
    const arrowRef = React.useRef<HTMLDivElement>(null)
    const openTimer = React.useRef<number | null>(null)
    const closeTimer = React.useRef<number | null>(null)
    // Track mount status so a setTimeout fired between schedule and unmount
    // doesn't try to setOpen on an unmounted component (React 18 strict mode
    // double-mounts will trigger this otherwise).
    const isMountedRef = React.useRef(true)

    const placement = positionToPlacement[position] ?? 'top'

    const {
        refs,
        floatingStyles,
        middlewareData,
        placement: actualPlacement,
    } = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        // `fixed` is correct when we portal to document.body — it positions
        // relative to the viewport and avoids drifting if any ancestor
        // becomes a containing block (transform, filter, contain, etc.).
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetMiddleware(8),
            flip({ fallbackAxisSideDirection: 'start' }),
            shift({ padding: 8 }),
            arrow({ element: arrowRef }),
        ],
    })

    React.useImperativeHandle(forwardedRef, () => refs.reference.current as HTMLElement)

    const clearTimers = () => {
        if (openTimer.current) {
            window.clearTimeout(openTimer.current)
            openTimer.current = null
        }
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const scheduleOpen = () => {
        if (disabled) return
        clearTimers()
        onOpening?.()
        openTimer.current = window.setTimeout(
            () => {
                if (!isMountedRef.current) return
                setOpen(true)
                onOpened?.()
            },
            Math.max(0, hoverOpenDelay),
        )
    }

    const scheduleClose = () => {
        clearTimers()
        closeTimer.current = window.setTimeout(
            () => {
                if (!isMountedRef.current) return
                setOpen(false)
                onClosed?.()
            },
            Math.max(0, hoverCloseDelay),
        )
    }

    const toggleClick = () => {
        if (disabled) return
        if (open) {
            setOpen(false)
            onClosed?.()
        } else {
            onOpening?.()
            setOpen(true)
            onOpened?.()
        }
    }

    React.useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            clearTimers()
        }
    }, [])

    // Click-away handler when interactionKind === 'click'
    React.useEffect(() => {
        if (interactionKind !== 'click' || !open) return
        const onDocClick = (event: MouseEvent) => {
            const refEl = refs.reference.current as HTMLElement | null
            const floatEl = refs.floating.current
            const t = event.target as Node
            if (refEl?.contains(t) || floatEl?.contains(t)) return
            setOpen(false)
            onClosed?.()
        }
        document.addEventListener('mousedown', onDocClick)
        return () => document.removeEventListener('mousedown', onDocClick)
    }, [interactionKind, open, refs.reference, refs.floating, onClosed])

    const triggerHandlers: React.HTMLAttributes<HTMLElement> = {}
    if (interactionKind === 'click') {
        triggerHandlers.onClick = toggleClick
    } else {
        triggerHandlers.onMouseEnter = scheduleOpen
        triggerHandlers.onMouseLeave = scheduleClose
        if (openOnTargetFocus) {
            triggerHandlers.onFocus = scheduleOpen
            triggerHandlers.onBlur = scheduleClose
        }
    }

    // Hover-kind 'hover' (Blueprint default for Tooltip2 is 'hover-target', but
    // 'hover' should keep the tooltip open while the cursor is over the popover).
    const popoverHoverHandlers: React.HTMLAttributes<HTMLElement> = {}
    if (interactionKind === 'hover') {
        popoverHoverHandlers.onMouseEnter = clearTimers
        popoverHoverHandlers.onMouseLeave = scheduleClose
    }

    const Wrapper = wrapperTagName as React.ElementType
    const arrowSide: 'top' | 'right' | 'bottom' | 'left' =
        (actualPlacement.split('-')[0] as 'top' | 'right' | 'bottom' | 'left') || 'top'
    const arrowOpposite = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' } as const
    const arrowX = middlewareData.arrow?.x
    const arrowY = middlewareData.arrow?.y

    // Blueprint convention: `Classes.POPOVER2_CONTENT_SIZING` ('bp4-popover2-
    // content-sizing') sets `max-width: 350px; padding: 20px` on the popover
    // content. Blueprint applies it via a nested selector that won't match our
    // DOM, so emit the equivalent Tailwind utilities ourselves whenever the
    // class is present. Lets callsites keep passing the Blueprint constant
    // during the migration without visual regression.
    const hasContentSizing =
        typeof popoverClassName === 'string' && popoverClassName.includes('popover2-content-sizing')

    // Suppress empty content (Blueprint also skips rendering when content is
    // null/undefined). Without this, callsites like `tooltip={user ? msg : ''}`
    // would render an empty styled rectangle on hover.
    const hasContent = content != null && content !== false && content !== ''
    const tooltipNode =
        open && !disabled && hasContent ? (
            <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 50 }}
                className={classNames(
                    'pointer-events-auto rounded-sm bg-[#404854] text-[12px] leading-[16px] text-white shadow-[0_2px_4px_rgba(17,20,24,0.2),0_8px_24px_rgba(17,20,24,0.2)]',
                    hasContentSizing ? 'max-w-[350px] p-5' : 'px-[10px] py-[5px]',
                    popoverClassName,
                )}
                role="tooltip"
                {...popoverHoverHandlers}
            >
                {content}
                <div
                    ref={arrowRef}
                    className="absolute h-2 w-2 rotate-45 bg-[#404854]"
                    style={{
                        left: arrowX != null ? `${arrowX}px` : undefined,
                        top: arrowY != null ? `${arrowY}px` : undefined,
                        [arrowOpposite[arrowSide]]: '-3px',
                    }}
                />
            </div>
        ) : null

    const portalTarget = portalContainer ?? (typeof document !== 'undefined' ? document.body : null)

    return (
        <>
            <Wrapper
                ref={refs.setReference as React.Ref<HTMLElement>}
                className={className}
                {...triggerHandlers}
            >
                {children}
            </Wrapper>
            {tooltipNode && portalTarget ? createPortal(tooltipNode, portalTarget) : tooltipNode}
        </>
    )
})

export { Tooltip }
